import { useMemo, useState, type ReactNode } from 'react';
import { Button, Caption1, MessageBar, MessageBarBody, Spinner, Switch, Text, tokens } from '@fluentui/react-components';
import {
  CheckmarkCircleRegular,
  DocumentRegular,
  PeopleAddRegular,
  PeopleTeamRegular,
  PersonAddRegular,
  ShieldCheckmarkRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { useSession } from '../auth/SessionProvider';
import { formatBytes } from '../util/bytes';
import type { DashboardCountDto, DashboardOverviewDto, LicenseDto } from '../api/types';
import './dashboard.css';

const RANGES = [
  { days: 1, label: '24 h' },
  { days: 7, label: '7 Tage' },
  { days: 30, label: '30 Tage' },
  { days: 90, label: '90 Tage' },
];

const ACTION_META: Record<string, { label: string; color: string }> = {
  CreateUser: { label: 'Benutzer angelegt', color: '#3a9b58' },
  BlockUser: { label: 'Benutzer gesperrt', color: '#e0a100' },
  DeleteUser: { label: 'Benutzer gelöscht', color: '#d13438' },
  ResetPassword: { label: 'Passwort zurückgesetzt', color: '#8764b8' },
  ResetMfa: { label: 'MFA zurückgesetzt', color: '#8764b8' },
  IssueTap: { label: 'TAP ausgestellt', color: '#8764b8' },
  CreateGroup: { label: 'Gruppe angelegt', color: '#3a9b58' },
  DeleteGroup: { label: 'Gruppe gelöscht', color: '#d13438' },
  AddMember: { label: 'Mitglied hinzugefügt', color: '#5b8def' },
  RemoveMember: { label: 'Mitglied entfernt', color: '#e0a100' },
  CreateTeam: { label: 'Team angelegt', color: '#3a9b58' },
  DeleteTeam: { label: 'Team gelöscht', color: '#d13438' },
  TeamGuestPolicy: { label: 'Team-Gastrichtlinie', color: '#5b8def' },
  ApprovalRequested: { label: 'Freigabe angefragt', color: '#5b8def' },
  ApprovalApproved: { label: 'Freigabe erteilt', color: '#3a9b58' },
  ApprovalRejected: { label: 'Freigabe abgelehnt', color: '#d13438' },
  AssessmentRun: { label: 'Assessment-Lauf', color: '#5b8def' },
  AssessmentCheckUpdated: { label: 'Prüfpunkt bearbeitet', color: '#878888' },
  PimRemove: { label: 'PIM-Zuweisung entfernt', color: '#d13438' },
  AssignLicense: { label: 'Lizenz zugewiesen', color: '#5b8def' },
};

function actionMeta(a: string) {
  return ACTION_META[a] ?? { label: a, color: '#878888' };
}

function n(value: number | null | undefined): string {
  return value == null ? '—' : String(value);
}

export function DashboardPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { me } = useSession();
  const isAdmin = me?.isAdmin ?? false;
  const firstName = me?.displayName?.split(' ')[0];

  const [days, setDays] = useState(7);
  const [onlyMine, setOnlyMine] = useState(false);

  const mine = useAsync(() => api.getMyApprovals(), [api]);
  const pending = useAsync(() => api.getPendingApprovals(), [api]);
  const overview = useAsync(() => (isAdmin ? api.getDashboardOverview(days) : Promise.resolve(null)), [api, isAdmin, days]);
  const licenses = useAsync(() => (isAdmin ? api.getLicenses() : Promise.resolve<LicenseDto[]>([])), [api, isAdmin]);
  const audit = useAsync(() => (isAdmin ? api.getAuditLog({ days, take: 100 }) : Promise.resolve([])), [api, isAdmin, days]);
  const odSummary = useAsync(() => (isAdmin ? api.getOneDriveSummary() : Promise.resolve(null)), [api, isAdmin]);

  const myOpen = (mine.data ?? []).filter((r) => r.status === 'Pending').length;
  const toApprove = (pending.data ?? []).length;

  const feed = useMemo(() => {
    const rows = audit.data ?? [];
    return onlyMine && me ? rows.filter((r) => r.actorUpn === me.userPrincipalName) : rows;
  }, [audit.data, onlyMine, me]);

  return (
    <div>
      <PageHeader
        title={firstName ? `Willkommen, ${firstName}` : 'Willkommen'}
        description="Überblick über deine Umgebung und Tätigkeiten."
      />

      {me && me.scopedAdministrativeUnits.length > 0 ? (
        <div style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBar intent="info">
            <MessageBarBody>
              Dein Wirkungsbereich ist eingegrenzt auf: <strong>{me.scopedAdministrativeUnits.join(', ')}</strong>.
            </MessageBarBody>
          </MessageBar>
        </div>
      ) : null}

      {/* Mein Bereich */}
      <div className="dash-row">
        <Tile
          title="Meine Anfragen"
          icon={<DocumentRegular />}
          value={myOpen}
          sub={`${(mine.data ?? []).length} gesamt · ${myOpen} offen`}
          onClick={() => navigate('/requests')}
        />
        {isAdmin || toApprove > 0 ? (
          <Tile
            title="Zu genehmigen"
            icon={<CheckmarkCircleRegular />}
            value={toApprove}
            sub={toApprove > 0 ? 'wartet auf dich' : 'nichts offen'}
            tone={toApprove > 0 ? '#e0a100' : undefined}
            onClick={() => navigate('/approvals')}
          />
        ) : null}
        <div className="dash-card" style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Schnellaktionen</Caption1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button size="small" icon={<PersonAddRegular />} onClick={() => navigate('/users/new')}>
              Benutzer
            </Button>
            <Button size="small" icon={<PeopleAddRegular />} onClick={() => navigate('/groups/new')}>
              Gruppe
            </Button>
            <Button size="small" icon={<PeopleTeamRegular />} onClick={() => navigate('/teams/new')}>
              Team
            </Button>
          </div>
        </div>
      </div>

      {isAdmin ? (
        <>
          <div className="dash-section-title">Umgebung</div>
          {overview.loading ? (
            <Spinner label="Lade Überblick…" />
          ) : overview.error ? (
            <MessageBar intent="error">
              <MessageBarBody>{overview.error.message}</MessageBarBody>
            </MessageBar>
          ) : overview.data ? (
            <Environment data={overview.data} licenses={licenses.data ?? []} onOpenAssessment={() => navigate('/assessment')} />
          ) : null}

          {odSummary.data ? (
            <>
              <div className="dash-section-title">OneDrive-Speicher</div>
              <div className="dash-kpis" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                <div className="dash-kpi">
                  <div className="dash-kpi-value">{formatBytes(odSummary.data.totalUsedBytes)}</div>
                  <div className="dash-kpi-label">Gesamt genutzt</div>
                </div>
                <div className="dash-kpi">
                  <div className="dash-kpi-value">{odSummary.data.count.toLocaleString('de-DE')}</div>
                  <div className="dash-kpi-label">OneDrives</div>
                </div>
                <div className="dash-kpi">
                  <div className="dash-kpi-value" style={odSummary.data.nearQuotaCount > 0 ? { color: '#d13438' } : undefined}>
                    {odSummary.data.nearQuotaCount}
                  </div>
                  <div className="dash-kpi-label">Nahe Quota (≥85%)</div>
                </div>
                <div className="dash-kpi">
                  <div className="dash-kpi-value pii" style={{ fontSize: 15 }}>{odSummary.data.topOwnerName ?? '—'}</div>
                  <div className="dash-kpi-label">Größter Verbraucher · {formatBytes(odSummary.data.topUsedBytes)}</div>
                </div>
              </div>
              <div style={{ marginTop: 8, marginBottom: 4 }}>
                <Button size="small" appearance="secondary" onClick={() => navigate('/onedrive')}>
                  Zur OneDrive-Übersicht
                </Button>
              </div>
            </>
          ) : null}

          <div className="dash-section-title">Aktivität &amp; Änderungen</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <div className="dash-seg" role="radiogroup" aria-label="Zeitraum">
              {RANGES.map((r) => (
                <button
                  key={r.days}
                  type="button"
                  role="radio"
                  aria-checked={r.days === days}
                  className={r.days === days ? 'dash-seg-item selected' : 'dash-seg-item'}
                  onClick={() => setDays(r.days)}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <Switch checked={onlyMine} onChange={(_, d) => setOnlyMine(d.checked)} label="nur meine" />
          </div>

          {overview.data ? <Activity data={overview.data} /> : null}

          <div className="dash-card" style={{ marginTop: 12 }}>
            {audit.loading ? (
              <Spinner size="tiny" label="Lade Aktivitäten…" />
            ) : feed.length === 0 ? (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Keine Aktivitäten im gewählten Zeitraum{onlyMine ? ' (nur meine)' : ''}.
              </Caption1>
            ) : (
              <div className="dash-feed">
                {feed.map((e) => {
                  const m = actionMeta(e.action);
                  return (
                    <div key={e.id} className="dash-feed-row">
                      <span className="dash-feed-dot" style={{ background: m.color }} />
                      <div className="dash-feed-main">
                        <div className="dash-feed-line">
                          <strong>{m.label}</strong>
                          {e.targetDisplay ? <span className="pii"> · {e.targetDisplay}</span> : null}
                          {e.result !== 'Success' ? <span style={{ color: '#d13438' }}> · {e.result}</span> : null}
                        </div>
                        <div className="dash-feed-sub pii">{e.actorUpn}</div>
                      </div>
                      <span className="dash-feed-time">{timeAgo(e.timestampUtc)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Tile({
  title,
  icon,
  value,
  sub,
  tone,
  onClick,
}: {
  title: string;
  icon: ReactNode;
  value: number;
  sub: string;
  tone?: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="dash-tile" onClick={onClick}>
      <span className="dash-tile-head">
        {icon}
        {title}
      </span>
      <span className="dash-tile-value" style={tone ? { color: tone } : undefined}>
        {value}
      </span>
      <span className="dash-tile-sub">{sub}</span>
    </button>
  );
}

function Environment({
  data,
  licenses,
  onOpenAssessment,
}: {
  data: DashboardOverviewDto;
  licenses: LicenseDto[];
  onOpenAssessment: () => void;
}) {
  const members = data.users.members ?? 0;
  const guests = data.users.guests ?? 0;
  const sec = data.security;
  const topLicenses = [...licenses]
    .filter((l) => l.total > 0)
    .sort((a, b) => b.consumed - a.consumed)
    .slice(0, 6);

  return (
    <div className="dash-split">
      <div className="dash-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="dash-kpis">
          <Kpi label="Mitglieder" value={n(data.users.members)} />
          <Kpi label="Gäste" value={n(data.users.guests)} />
          <Kpi label="Gruppen" value={n(data.groups)} />
          <Kpi label="Teams" value={n(data.teams)} />
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <Donut members={members} guests={guests} />
          <div className="dash-bars" style={{ flex: 1, minWidth: 200 }}>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Lizenz-Auslastung</Caption1>
            {topLicenses.length === 0 ? (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Keine Lizenzdaten.</Caption1>
            ) : (
              topLicenses.map((l) => {
                const pct = l.total > 0 ? Math.round((100 * l.consumed) / l.total) : 0;
                const color = pct >= 90 ? '#d13438' : pct >= 75 ? '#e0a100' : tokens.colorBrandBackground;
                return (
                  <div key={l.skuId}>
                    <div className="dash-bar-head">
                      <span title={l.displayName}>{l.displayName}</span>
                      <span>
                        {l.consumed}/{l.total}
                      </span>
                    </div>
                    <div className="dash-bar-track">
                      <div className="dash-bar-fill" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className="dash-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          <ShieldCheckmarkRegular style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Sicherheits-Highlights
        </Caption1>
        <div style={{ display: 'flex', gap: 10 }}>
          <Kpi label="Global Admins" value={n(sec.globalAdmins)} tone={(sec.globalAdmins ?? 0) > 4 ? '#e0a100' : undefined} />
          <Kpi
            label="offene Hoch-Risiken"
            value={sec.openHighRisks == null ? '—' : String(sec.openHighRisks)}
            tone={(sec.openHighRisks ?? 0) > 0 ? '#d13438' : undefined}
          />
        </div>
        {sec.lastRunUtc ? (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            Letztes Assessment: <strong>{sec.lastRunScore}%</strong> · {new Date(sec.lastRunUtc).toLocaleDateString('de-DE')}
          </Caption1>
        ) : (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Noch kein Assessment gelaufen.</Caption1>
        )}
        <Button size="small" appearance="secondary" onClick={onOpenAssessment}>
          Zum M365-Assessment
        </Button>
      </div>
    </div>
  );
}

function Activity({ data }: { data: DashboardOverviewDto }) {
  const a = data.activity;
  const max = Math.max(1, ...a.byDay.map((d) => d.count));
  const step = Math.max(1, Math.ceil(a.byDay.length / 12));

  const count = (key: string) => a.byAction.find((x: DashboardCountDto) => x.key === key)?.count ?? 0;

  return (
    <div className="dash-split">
      <div className="dash-card">
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Ereignisse je Tag ({a.total} gesamt)</Caption1>
        <div className="dash-days" style={{ marginTop: 8 }}>
          {a.byDay.map((d, i) => (
            <div className="dash-day" key={d.date} title={`${formatDay(d.date)}: ${d.count}`}>
              <div className="dash-day-fill" style={{ height: `${Math.max(2, (d.count / max) * 64)}px` }} />
              <span className="dash-day-label">{i % step === 0 ? formatDay(d.date) : ''}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="dash-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Im Zeitraum</Caption1>
        <div className="dash-kpis" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <Kpi label="Benutzer angelegt" value={String(count('CreateUser'))} />
          <Kpi label="Teams erstellt" value={String(count('CreateTeam'))} />
          <Kpi label="Gruppen erstellt" value={String(count('CreateGroup'))} />
          <Kpi label="Freigaben erteilt" value={String(count('ApprovalApproved'))} />
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="dash-kpi">
      <div className="dash-kpi-value" style={tone ? { color: tone } : undefined}>
        {value}
      </div>
      <div className="dash-kpi-label">{label}</div>
    </div>
  );
}

function Donut({ members, guests }: { members: number; guests: number }) {
  const total = members + guests || 1;
  const r = 46;
  const c = 2 * Math.PI * r;
  const memLen = (members / total) * c;
  return (
    <svg viewBox="0 0 120 120" width="116" height="116" style={{ flexShrink: 0 }}>
      <g transform="rotate(-90 60 60)">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#5b8def" strokeWidth="14" strokeDasharray={`${memLen} ${c - memLen}`} />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke="#e0a100"
          strokeWidth="14"
          strokeDasharray={`${c - memLen} ${memLen}`}
          strokeDashoffset={-memLen}
        />
      </g>
      <text x="60" y="58" textAnchor="middle" fontSize="20" fontWeight="700" fill="currentColor">
        {members + guests}
      </text>
      <text x="60" y="75" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.55">
        Konten
      </text>
    </svg>
  );
}

function formatDay(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `vor ${days} T`;
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}
